import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Article } from "@domain/lms/article/article";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";
import { FavoriteOffIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";

interface ArticleThumbnailMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
	blur?: boolean;
}

export const ArticleThumbnailMenu: React.FC<ArticleThumbnailMenuProps> = ({ article, blur, ...htmlProps }) => {
	const { articleService } = useServices();
	const askDeleteConfirmation = useConfirmation(
		I18n.t("article.delete.title"),
		I18n.t("delete"),
		I18n.t("article.delete.message")
	);
	const history = useHistory();
	const currentProfile = useCurrentProfile();
	const displayedAuthor = getDisplayedAuthor(article);
	const subAuthor = displayedAuthor !== article.author ? article.author : undefined;
	const isAuthor = displayedAuthor.id === currentProfile?.id || subAuthor?.id === currentProfile?.id;
	const [favorite, setFavorite] = useState(article.isFavorite);
	const { createModal } = useModalTools();

	return (
		<KebabMenu blur={blur} {...htmlProps} authOnly>
			<SimpleMenuContent
				onShare={
					article.status === "DRAFT"
						? undefined
						: () => {
								return createModal((close) => (
									<EditReferencePostModal close={close} shared={{ type: "SHARE_ARTICLE", reference: article }} />
								));
							}
				}
				onEdit={isAuthor ? () => history.push(Path.ArticleEditor(article.id)) : undefined}
				onDelete={
					isAuthor
						? () =>
								askDeleteConfirmation(async () => {
									await articleService.deleteArticle(article.id);
									createToast(I18n.t("article.toast.delete.title"), I18n.t("article.toast.delete.message"), "success");
									history.push(Path.Root);
								})
						: undefined
				}
				reportInfos={
					isAuthor
						? undefined
						: {
								contentId: article.id,
								contentType: "ARTICLE",
							}
				}
			>
				{!isAuthor && (
					<MenuRow
						onClick={async () => {
							await articleService.toggleFavorite({ ...article, isFavorite: favorite });
							setFavorite(!favorite);
						}}
					>
						<IconWrapper>
							<FavoriteOffIcon toggled={favorite} />
						</IconWrapper>
						<span>{I18n.t("favorite")}</span>
					</MenuRow>
				)}
			</SimpleMenuContent>
		</KebabMenu>
	);
};

const IconWrapper = styled.div`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};

	> svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;
